import { atom, useAtomValue } from 'jotai'

/**
 * The new design, on by default, with `?new_design=0` as the way out.
 *
 * `?new_design=0` turns it off and remembers that; `?new_design=1` turns it
 * back on and forgets the opt-out. Anything else leaves the stored preference
 * alone, so the choice survives navigation and reloads without having to be
 * carried in every link.
 *
 * This mirrors how the app already handles language: i18n.ts:51-56 detects
 * `?i18nlng=` querystring-first and caches to localStorage. Same contract, so
 * there is one idiom for "URL param flips behaviour and sticks", not two.
 *
 * What is stored is the opt-OUT, the literal string 'false'. It used to be the
 * opt-IN ('true'), so anyone who had already opted in reads back 'true', which
 * is not 'false', and stays on the new design. Everyone else has no key at all
 * and now gets it.
 *
 * Read at module load, BEFORE React mounts, because the shell decides which
 * layout to render on its first pass - a value that arrived a tick later would
 * mount the old shell and then throw it away.
 */
const STORAGE_KEY = 'cstln_new_design'
export const QUERY_PARAM = 'new_design'

function resolve(): boolean {
  // No DOM means no querystring and no storage to consult, so it falls to the
  // default, which is on.
  if (typeof window === 'undefined') return true

  const raw = new URLSearchParams(window.location.search).get(QUERY_PARAM)

  // Only the two explicit values are commands. A typo falls through to the
  // stored preference rather than silently clearing someone's opt-out.
  if (raw === '0' || raw === 'false' || raw === '1' || raw === 'true') {
    const off = raw === '0' || raw === 'false'
    try {
      if (off) window.localStorage.setItem(STORAGE_KEY, 'false')
      else window.localStorage.removeItem(STORAGE_KEY)
    } catch {
      /* private mode / storage disabled - the URL still wins for this page */
    }
    return !off
  }

  try {
    return window.localStorage.getItem(STORAGE_KEY) !== 'false'
  } catch {
    return true
  }
}

const enabled = resolve()

/** Sync read, for module scope and non-React code. */
export function isNewDesign(): boolean {
  return enabled
}

export const newDesignAtom = atom(enabled)

/** React read. Constant for the lifetime of the page by design - see resolve(). */
export function useNewDesign(): boolean {
  return useAtomValue(newDesignAtom)
}
